import { getApiErrorMessage, orderAPI } from '@matix/api-client';
import { useCart } from '../../store/cart';
import { orderTypeCodeFor } from '../../lib/orderTypeCode';
import { roundMoney } from '@matix/utils';
import { moneyPrecision } from '../../store/currency';
import { buildCreateOrderRequest, saveHeldOrder } from '../orders/usePlaceOrder';
import { isNetworkError } from '../../lib/netStatus';
import { heldErrorText } from './heldEdit';

/**
 * HOLD the cart — the legacy till's "Hold", which writes a real invoice with
 * `IsHolded = 1` on the Unpaid tender. It shows on the Held Orders screen,
 * where it is printed, paid or cancelled, and it goes to the kitchen now.
 *
 * Three outcomes the caller acts on:
 *   - `held`     the invoice exists (new, or an already-held one re-saved);
 *                the cart can be cleared.
 *   - `local`    the server cannot take it as an invoice — offline, or a cart
 *                a held invoice cannot represent (delivery, membership, a
 *                return). The caller keeps the old local hold, so nothing the
 *                cashier typed is ever lost.
 *   - `refused`  the server said no for a reason the cashier must fix (a
 *                coupon, points, a gift card). `message` says what.
 */
export type HoldOutcome =
  | { kind: 'held'; receiptId: number; entryNo: number; total: number; updated: boolean }
  | { kind: 'local' }
  | { kind: 'refused'; message: Bilingual | string };

/** Both scripts of a message the caller shows with the till's language. */
export interface Bilingual {
  en: string;
  ar: string;
}

function newKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // RFC 4122 v4 from Math.random — only reached on very old WebViews.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** A cart the held-invoice path cannot carry — kept on the local hold instead. */
export function needsLocalHold(): boolean {
  const s = useCart.getState();
  return (
    s.orderMode === 'delivery' ||
    s.orderMode === 'membership' ||
    s.lines.some((l) => l.isReturn) ||
    s.exchangeParentId != null ||
    // Editing an already-PAID receipt: holding it is not a thing a paid
    // invoice can become. Unchanged behaviour — it goes to the local hold.
    (s.editingReceiptId != null && !s.editingHeld)
  );
}

export async function holdCart(
  branchId: number,
  /**
   * readyBy: a LAUNDRY take-in's promised date (ISO), written to
   * CustomerDeliverDate through the order's top-level delivery_date.
   */
  opts: { readyBy?: string | null } = {},
): Promise<HoldOutcome> {
  if (needsLocalHold()) return { kind: 'local' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'local' };

  const s = useCart.getState();
  const t = s.totals();
  const rm = (n: number): number => roundMoney(n, moneyPrecision());

  if (s.loyaltyRedeem && s.loyaltyRedeem.points > 0) {
    return { kind: 'refused', message: { en: 'Remove the points redemption before holding — redeem them at payment.', ar: 'أزل استبدال النقاط قبل التعليق — استبدلها عند الدفع.' } };
  }
  if (s.discount?.couponCode && s.discount.serverPriced) {
    return { kind: 'refused', message: { en: 'Remove the coupon before holding — apply it at payment.', ar: 'أزل الكوبون قبل التعليق — طبّقه عند الدفع.' } };
  }
  if (s.lines.some((l) => l.giftCardIssue)) {
    return { kind: 'refused', message: { en: 'A gift card sale cannot be held. Charge it now.', ar: 'لا يمكن تعليق بيع بطاقة هدية. حصّل المبلغ الآن.' } };
  }

  const headerDiscountValue = rm(t.discount);
  const req = buildCreateOrderRequest({
    idempotencyKey: newKey(),
    branchId,
    cartLines: s.lines,
    customer: s.customer,
    customerAddressId: s.customerAddressId,
    // A placeholder: the server replaces it with the Unpaid tender at its own
    // computed net, which includes the automatic promotions the till leaves out.
    payments: [{ type_code: -1, amount: rm(t.total) }],
    ...(headerDiscountValue > 0 ? { headerDiscountValue } : {}),
    orderTypeCode: orderTypeCodeFor(s.orderMode, s.orderType),
    ...(s.discount?.discountReasonId != null ? { discountReasonId: s.discount.discountReasonId } : {}),
    ...(s.discount?.couponCode && !s.discount.serverPriced ? { couponCode: s.discount.couponCode } : {}),
    ...(s.table ? { table: s.table } : {}),
  });
  if (opts.readyBy) req.delivery_date = opts.readyBy;

  try {
    // Already held and reopened: save the changes onto the same invoice.
    if (s.editingReceiptId != null && s.editingHeld) {
      const r = await saveHeldOrder(s.editingReceiptId, req);
      return { kind: 'held', receiptId: r.order_id, entryNo: r.entry_no, total: r.totals.net_value, updated: true };
    }
    const r = await orderAPI.create({ ...req, hold: true }, { timeoutMs: 15_000 });
    return { kind: 'held', receiptId: r.order_id, entryNo: r.entry_no, total: r.totals.net_value, updated: false };
  } catch (err) {
    // No answer. The invoice MAY have been written — a timeout says nothing
    // about that — so keeping a local copy as well could hold it twice. The
    // cart is left as it is and the cashier is told where to look.
    if (isNetworkError(err)) {
      return {
        kind: 'refused',
        message: {
          en: 'No answer from the server. Check Held Orders before holding this again.',
          ar: 'لا يوجد رد من الخادم. راجع الطلبات المعلقة قبل تعليق هذا الطلب مرة أخرى.',
        },
      };
    }
    return {
      kind: 'refused',
      message:
        heldErrorText(err) ||
        (err instanceof Error && !('response' in err) ? err.message : getApiErrorMessage(err)) ||
        { en: 'The order could not be held.', ar: 'تعذّر تعليق الطلب.' },
    };
  }
}
